import React, { useEffect, useRef } from "react";
import Header from "./Header";
import Footer from "./Footer";
import "./AboutUs.css";
import aboutImg1 from '../assets/about-img1.jpg';
import aboutImg2 from '../assets/about-img2.jpg';
import aboutImg3 from '../assets/about-img3.jpg';
import { Helmet } from "react-helmet";


<Helmet>
  <title>About Kora Insurance | Our Mission to Simplify Insurance </title>
  <meta name="description" content="Learn about YourBrand’s mission to simplify insurance and serve customers with transparency and trust." />
  <meta name="keywords" content="Insurance Ghana, Family Insurance, Affordable Cover, Kora Insurance" />
  <meta name="robots" content="index, follow" />
  <link rel="canonical" href="https://korame.com.gh"/>
  {/* Open Graph tags */}
  <meta property="og:title" content="Best Online Insurance Platform" />
  <meta property="og:description" content="Compare and buy insurance plans with ease." />
  <meta property="og:url" content="https://korame.com.gh/" />
  <meta property="og:type" content="website" />
</Helmet>

const AboutUs = () => {
  const parallaxRefs = useRef([]);

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.pageYOffset;
      
      parallaxRefs.current.forEach((element) => {
        if (element) {
          const parent = element.parentElement;
          const parentRect = parent.getBoundingClientRect();
          const isVisible = 
            parentRect.top < window.innerHeight && 
            parentRect.bottom > 0;

          if (isVisible) {
            const speed = element.dataset.speed || 0.15;
            const yPos = (parentRect.top * speed);
            element.style.transform = `translate3d(0, ${yPos}px, 0)`;
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial position

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const addToRefs = (el) => {
    if (el && !parallaxRefs.current.includes(el)) {
      parallaxRefs.current.push(el);
    }
  };

  return (
    <div className="about-page" style={{ overflowX: 'hidden' }}>
      <Header />
      <div className="about-container" style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '80px 1rem 2rem',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <h1 className="page-title" style={{
          fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
          textAlign: 'center',
          marginBottom: '1rem',
          width: '100%',
          wordWrap: 'break-word'
        }}>About Us</h1>
        <p className="page-subtitle" style={{
          fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
          textAlign: 'center',
          marginBottom: '3rem',
          color: '#666'
        }}>
          Discover how Kora is transforming insurance for Ghanaians.
        </p>

        <section className="about-section two-col-section" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '2rem',
          alignItems: 'center',
          marginBottom: '3rem',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div className="col-left" style={{
            width: '100%',
            minHeight: '300px',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <img
              ref={addToRefs}
              data-speed="0.1"
              src={aboutImg1}
              alt="Kora Overview"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '8px',
                maxWidth: '100%',
                willChange: 'transform',
                transition: 'transform 0.05s linear'
              }}
            />
          </div>
          <div className="col-right" style={{
            width: '100%',
            wordWrap: 'break-word'
          }}>
            <h2 style={{
              fontSize: 'clamp(1.5rem, 3vw, 2rem)',
              marginBottom: '1rem',
              width: '100%'
            }}>Who We Are</h2>
            <p style={{
              fontSize: 'clamp(0.9rem, 2vw, 1rem)',
              marginBottom: '1rem',
              lineHeight: '1.6',
              width: '100%'
            }}>
              <strong>Kora</strong> is a pioneering insurance product
              designed specifically for Ghanaians, made possible by the strategic
              alliance between <strong>Summer Health Limited</strong>, a reputable 
              healthcare technology company in Ghana, and <strong>SanlamAllianz</strong>, 
              a pan-African financial services leader.
            </p>
            <p style={{
              fontSize: 'clamp(0.9rem, 2vw, 1rem)',
              marginBottom: '1rem',
              lineHeight: '1.6',
              width: '100%'
            }}>
              By merging Summer Health's deep local insight and proven 
              healthcare technology expertise with SanlamAllianz's robust financial resources, 
              Kora aims to make quality insurance accessible and affordable 
              for everyone in Ghana.
            </p>
          </div>
        </section>

        <hr className="section-divider" />

        <section className="about-section single-col-section" style={{
          maxWidth: '800px',
          margin: '0 auto 3rem',
          padding: '2rem',
          background: '#fff',
          borderRadius: '8px',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <h2 style={{
            fontSize: 'clamp(1.5rem, 3vw, 2rem)',
            marginBottom: '1.5rem',
            textAlign: 'center'
          }}>Summer Health Limited at a Glance</h2>
          <ul style={{
            listStyle: 'none',
            padding: 0,
            margin: 0
          }}>
            <li style={{
              fontSize: 'clamp(0.85rem, 1.8vw, 1rem)',
              marginBottom: '1rem',
              lineHeight: '1.6'
            }}>
              <strong>Over Nine Years of Healthcare Excellence:</strong> Delivering 
              homecare services to clients who value receiving 
              care in the comfort of their homes or offices and streamlining medical consultation for clients who prefer to visit health professionals in their offices.
            </li>
            <li style={{
              fontSize: 'clamp(0.85rem, 1.8vw, 1rem)',
              marginBottom: '1rem',
              lineHeight: '1.6'
            }}>
              <strong>Patient-Centered Values:</strong> Prioritizing compassion, 
              competence, and affordability, ensuring that every client receives 
              tailored, quality care.
            </li>
            <li style={{
              fontSize: 'clamp(0.85rem, 1.8vw, 1rem)',
              marginBottom: '1rem',
              lineHeight: '1.6'
            }}>
              <strong>Mission Statement:</strong> To make affordable healthcare accessible in sub-Saharan Africa.
            </li>
            <li style={{
              fontSize: 'clamp(0.85rem, 1.8vw, 1rem)',
              marginBottom: '1rem',
              lineHeight: '1.6'
            }}>
              Through years of frontline experience, Summer Health has earned trust 
              and recognition for its commitment to healthcare innovation and 
              patient-focused solutions.
            </li>
          </ul>
        </section>

        <hr className="section-divider" />

        <section className="about-section two-col-section reverse-layout" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '2rem',
          alignItems: 'center',
          marginBottom: '3rem',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div className="col-left">
            <h2 style={{
              fontSize: 'clamp(1.5rem, 3vw, 2rem)',
              marginBottom: '1rem'
            }}>SanlamAllianz: A Pan-African Financial Powerhouse</h2>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0
            }}>
              <li style={{
                fontSize: 'clamp(0.85rem, 1.8vw, 1rem)',
                marginBottom: '1rem',
                lineHeight: '1.6'
              }}>
                <strong>Joint Venture of Global Leaders:</strong> Formed by Sanlam, 
                a prominent non-banking financial services provider in Africa, and Allianz, 
                a worldwide insurance and asset management pioneer.
              </li>
              <li style={{
                fontSize: 'clamp(0.85rem, 1.8vw, 1rem)',
                marginBottom: '1rem',
                lineHeight: '1.6'
              }}>
                <strong>Operations in 27 Countries:</strong> Spearheading insurance 
                solutions across Africa, ensuring robust financial backing and broad 
                market insight.
              </li>
              <li style={{
                fontSize: 'clamp(0.85rem, 1.8vw, 1rem)',
                marginBottom: '1rem',
                lineHeight: '1.6'
              }}>
                <strong>Vision &amp; Purpose:</strong> To empower generations with 
                financial confidence, security, and prosperity, anchored in excellence 
                and sustainable growth.
              </li>
            </ul>
          </div>
          <div className="col-right" style={{
            overflow: 'hidden',
            position: 'relative'
          }}>
            <img
              ref={addToRefs}
              data-speed="0.15"
              src={aboutImg2}
              alt="SanlamAllianz Partnership"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '8px',
                willChange: 'transform',
                transition: 'transform 0.05s linear'
              }}
            />
          </div>
        </section>

        <hr className="section-divider" />

        <section className="about-section two-col-section" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '2rem',
          alignItems: 'center',
          marginBottom: '3rem',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div className="col-left" style={{
            overflow: 'hidden',
            position: 'relative'
          }}>
            <img
              ref={addToRefs}
              data-speed="0.12"
              src={aboutImg3}
              alt="Why Partnership Matters"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '8px',
                willChange: 'transform',
                transition: 'transform 0.05s linear'
              }}
            />
          </div>
          <div className="col-right">
            <h2 style={{
              fontSize: 'clamp(1.5rem, 3vw, 2rem)',
              marginBottom: '1rem'
            }}>
              Why This Partnership Matters for Ghanaians
            </h2>
            <p style={{
              fontSize: 'clamp(0.9rem, 2vw, 1rem)',
              marginBottom: '1rem',
              lineHeight: '1.6'
            }}>
              <strong>Healthcare + Insurance Synergy:</strong> By pairing Summer Health's rich insight into patient care with SanlamAllianz's robust financial and insurance capabilities, we bridge the gap between receiving quality healthcare and having the financial safety net to support it.
            </p>
            <p style={{
              fontSize: 'clamp(0.9rem, 2vw, 1rem)',
              marginBottom: '1rem',
              lineHeight: '1.6'
            }}>
              <strong>Localized Solutions:</strong> Developed and launched in Ghana, 
              Kora caters specifically to Ghanaians' healthcare challenges and 
              financial realities, offering practical, user-friendly coverage plans.
            </p>
            <p style={{
              fontSize: 'clamp(0.9rem, 2vw, 1rem)',
              marginBottom: '1rem',
              lineHeight: '1.6'
            }}>
              <strong>Affordability &amp; Accessibility:</strong> Staying true to 
              Summer Health's principle of affordable care, Kora provides 
              cost-effective insurance options, reducing out-of-pocket healthcare 
              expenses.
            </p>
            <p style={{
              fontSize: 'clamp(0.9rem, 2vw, 1rem)',
              marginBottom: '1rem',
              lineHeight: '1.6'
            }}>
              <strong>Closing Gaps Left by NHIS:</strong> While the National Health 
              Insurance Scheme (NHIS) has expanded basic healthcare access, there 
              are still coverage gaps that leave many out-of-pocket expenses 
              uncovered. Kora offers an extra layer of financial protection 
              and additional benefits, complementing NHIS and helping individuals 
              and families achieve more comprehensive healthcare security.
            </p>
            <p style={{
              fontSize: 'clamp(0.9rem, 2vw, 1rem)',
              marginBottom: '1rem',
              lineHeight: '1.6'
            }}>
              <strong>Innovation &amp; Growth:</strong> SanlamAllianz's commitment 
              to digital innovation ensures that Ghanaians benefit from streamlined 
              claims, efficient underwriting, and greater access to micro-insurance—
              without unnecessary red tape.
            </p>
          </div>
        </section>

        <hr className="section-divider" />

        <section className="about-section single-col-section" style={{
          maxWidth: '800px',
          margin: '0 auto 3rem',
          padding: '2rem',
          background: '#fff',
          borderRadius: '8px',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <h2 style={{
            fontSize: 'clamp(1.5rem, 3vw, 2rem)',
            marginBottom: '1.5rem',
            textAlign: 'center'
          }}>Our Commitment</h2>
          <ul style={{
            listStyle: 'none',
            padding: 0,
            margin: 0
          }}>
            <li style={{
              fontSize: 'clamp(0.85rem, 1.8vw, 1rem)',
              marginBottom: '1rem',
              lineHeight: '1.6'
            }}>
              <strong>Inclusive Coverage:</strong> Ensuring Ghanaians of all income 
              levels and backgrounds can access insurance that fits their needs.
            </li>
            <li style={{
              fontSize: 'clamp(0.85rem, 1.8vw, 1rem)',
              marginBottom: '1rem',
              lineHeight: '1.6'
            }}>
              <strong>Quality Healthcare Insight:</strong> Drawing on Summer Health's 
              proven track record in patient care to structure benefits that truly 
              support your wellbeing.
            </li>
            <li style={{
              fontSize: 'clamp(0.85rem, 1.8vw, 1rem)',
              marginBottom: '1rem',
              lineHeight: '1.6'
            }}>
              <strong>Pan-African Expertise, Local Focus:</strong> While SanlamAllianz 
              spans multiple African countries, Kora is laser-focused on Ghana's 
              unique healthcare landscape.
            </li>
            <li style={{
              fontSize: 'clamp(0.85rem, 1.8vw, 1rem)',
              marginBottom: '1rem',
              lineHeight: '1.6'
            }}>
              <strong>Community Empowerment:</strong> We believe that securing one's 
              health through reliable insurance fosters resilience, stability, 
              and long-term prosperity.
            </li>
          </ul>
        </section>

        <hr className="section-divider" />

        <section className="about-section two-col-section reverse-layout" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '2rem',
          alignItems: 'center',
          marginBottom: '3rem'
        }}>
          <div className="col-left">
            <h2 style={{
              fontSize: 'clamp(1.5rem, 3vw, 2rem)',
              marginBottom: '1rem'
            }}>Looking Ahead</h2>
            <p style={{
              fontSize: 'clamp(0.9rem, 2vw, 1rem)',
              marginBottom: '1rem',
              lineHeight: '1.6'
            }}>
              Kora
              <em> in Akan means "protect"</em> and that is the foundation of our mission. 
              We believe security is not a privilege but a necessity, no matter the scale of your needs, 
              we are committed to safeguarding your future. While our primary focus is providing accessible
               and reliable insurance for Ghanaians, our vision extends beyond borders. 
               We strive to create a future where protection is not just a policy, but a promise.
            </p>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
};

export default AboutUs;
